import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../_services/alert.service';
import { SupplierService } from '../../_services/supplier.service';
import { Supplier } from '../../_interfaces/supplier';
import { PaginatorPagesDirective } from '../../../Shared/_directives/paginator-pages.directive';

// Backed by SupplierService (/suppliers/*). Not paginated server-side -
// the supplier count is small enough that the whole list loads at once and
// is searched, sorted and paged here, unlike Purchases or Inventory.
@Component({
    selector: 'app-suppliers',
    imports: [PaginatorPagesDirective,
        MainLayoutComponent,
        MatIconModule,
        MatTooltipModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        MatPaginatorModule,
        FormsModule,
        CommonModule,
    ],
    templateUrl: './suppliers.component.html',
    styleUrl: './suppliers.component.css'
})
export class SuppliersComponent implements OnInit {
  loading = true;
  loadError = false;

  suppliers: Supplier[] = [];
  name = '';
  sort: 'NAME' | 'ORDERS' | 'RECENT' = 'NAME';
  pageIndex = 0;
  pageSize = 12;

  readonly sortOptions = [
    { key: 'NAME', label: 'Name A-Z' },
    { key: 'ORDERS', label: 'Most orders' },
    { key: 'RECENT', label: 'Ordered most recently' },
  ] as const;

  constructor(
    private router: Router,
    private alertService: AlertService,
    private supplierService: SupplierService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.supplierService.getSuppliers().subscribe({
      next: (suppliers) => {
        this.suppliers = suppliers;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
        this.alertService.alert('Could not load suppliers', 'error');
      },
    });
  }

  // Search as you type, across everything worth searching on.
  get filtered(): Supplier[] {
    const words = this.name.toLowerCase().split(/\s+/).filter(Boolean);
    const list = this.suppliers.filter((s) => {
      const hay = [s.name, s.email, s.phone, s.city, s.country, s.vatNumber, s.postcode]
        .map((v) => String(v ?? '').toLowerCase())
        .join(' ');
      return words.every((w) => hay.includes(w));
    });
    return [...list].sort((a, b) => {
      if (this.sort === 'ORDERS') return (b.orderCount ?? 0) - (a.orderCount ?? 0) || a.name.localeCompare(b.name);
      if (this.sort === 'RECENT') {
        return new Date(b.lastOrderDate ?? 0).getTime() - new Date(a.lastOrderDate ?? 0).getTime() || a.name.localeCompare(b.name);
      }
      return a.name.localeCompare(b.name);
    });
  }

  get page(): Supplier[] {
    const start = this.pageIndex * this.pageSize;
    return this.filtered.slice(start, start + this.pageSize);
  }

  get sortLabel(): string {
    return this.sortOptions.find((o) => o.key === this.sort)?.label ?? 'Sort';
  }

  get totals(): { suppliers: number; orders: number; open: number } {
    return {
      suppliers: this.suppliers.length,
      orders: this.suppliers.reduce((n, s) => n + (s.orderCount ?? 0), 0),
      open: this.suppliers.reduce((n, s) => n + (s.openOrders ?? 0), 0),
    };
  }

  onSearchChange(value: string): void {
    this.name = value;
    this.pageIndex = 0;
  }

  setSort(key: 'NAME' | 'ORDERS' | 'RECENT'): void {
    this.sort = key;
    this.pageIndex = 0;
  }

  onPage(event: PageEvent): void {
    if (this.pageSize !== event.pageSize) {
      this.pageSize = event.pageSize;
      this.pageIndex = 0;
    } else {
      this.pageIndex = event.pageIndex;
    }
  }

  initials(s: Supplier): string {
    const words = String(s.name ?? '?').trim().split(/\s+/);
    return ((words[0]?.[0] ?? '?') + (words[1]?.[0] ?? '')).toUpperCase();
  }

  private static readonly AVATARS = [
    'bg-amber-100 text-amber-800', 'bg-sky-100 text-sky-800', 'bg-emerald-100 text-emerald-800',
    'bg-violet-100 text-violet-800', 'bg-rose-100 text-rose-800', 'bg-orange-100 text-orange-800',
  ];

  avatarClass(s: Supplier): string {
    return SuppliersComponent.AVATARS[Number(s.id || 0) % SuppliersComponent.AVATARS.length];
  }

  addressLine(s: Supplier): string {
    return [s.addressLine1, s.city, s.postcode].filter(Boolean).join(', ');
  }

  mapsUrl(s: Supplier): string {
    const q = [s.addressLine1, s.addressLine2, s.city, s.postcode, s.country].filter(Boolean).join(', ');
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q);
  }

  websiteUrl(s: Supplier): string {
    const w = String(s.website ?? '').trim();
    return /^https?:\/\//i.test(w) ? w : `https://${w}`;
  }

  onCreateSupplier(): void {
    this.router.navigate(['/suppliers/create']);
  }

  onEdit(supplier: Supplier): void {
    this.router.navigate(['/suppliers/edit', supplier.id]);
  }
}
